import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { ProductSelector } from '@/features/catalog/components/ProductSelector';
import {
  EXPORT_MAX_PERIOD_DAYS,
  EXPORT_PASSWORD_MAX,
  EXPORT_PASSWORD_MIN,
  EXPORT_REASON_MAX,
  downloadCollectionExport,
  validateCollectionExport,
  type CollectionExportForm,
  type CollectionExportProblem,
} from '../lib/piiAccess';

interface ProductCollectionExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY: CollectionExportForm = { productId: null, from: '', to: '', reason: '', password: '' };

/**
 * 상품 추가입력 엑셀 내보내기(be-catalog b662243, `POST /admin/v1/product-collections/export`).
 * 개인정보 원문이 담긴 파일이라, 사유를 받아야 내려받을 수 있고 BE 감사로그에 사유·조건·행 수가 남는다(2년 보관).
 *
 * - 상품 또는 기간 중 하나는 필수. 상품 없이 기간만이면 최대 31일(양끝 포함). 최대 5000행(넘으면 BE 400 → 메시지 그대로).
 * - 비밀번호는 선택(8~64자)이지만 권장 — 주면 엑셀을 열 때 비밀번호를 묻는 암호화 파일이 된다.
 * - 사유·비밀번호는 이 화면 상태에만 두고, 닫으면 지운다(저장하지 않음).
 */
export const ProductCollectionExportDialog = ({ open, onOpenChange }: ProductCollectionExportDialogProps) => {
  const { t } = useTranslation('orders');
  const [form, setForm] = useState<CollectionExportForm>(EMPTY);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 닫으면 사유·비밀번호를 비운다 — 다음에 열 때 남아 있지 않게.
  useEffect(() => {
    if (!open) {
      setForm(EMPTY);
      setError(null);
    }
  }, [open]);

  const set = <K extends keyof CollectionExportForm>(key: K, value: CollectionExportForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const problemMessage = (p: CollectionExportProblem): string =>
    ({
      reasonRequired: t('orders.collectionExport.problem.reasonRequired', '다운로드 사유를 입력하세요.'),
      reasonTooLong: t('orders.collectionExport.problem.reasonTooLong', {
        max: EXPORT_REASON_MAX,
        defaultValue: '다운로드 사유는 {{max}}자 이하로 입력하세요.',
      }),
      scopeRequired: t('orders.collectionExport.problem.scopeRequired', '상품 또는 기간 중 하나는 지정하세요.'),
      periodPair: t('orders.collectionExport.problem.periodPair', '기간은 시작일과 종료일을 함께 지정하세요.'),
      periodOrder: t('orders.collectionExport.problem.periodOrder', '종료일이 시작일보다 빠릅니다.'),
      periodTooLong: t('orders.collectionExport.problem.periodTooLong', {
        max: EXPORT_MAX_PERIOD_DAYS,
        defaultValue: '상품을 지정하지 않으면 기간은 최대 {{max}}일입니다.',
      }),
      passwordLength: t('orders.collectionExport.problem.passwordLength', {
        min: EXPORT_PASSWORD_MIN,
        max: EXPORT_PASSWORD_MAX,
        defaultValue: '비밀번호는 {{min}}~{{max}}자로 입력하세요.',
      }),
    })[p];

  const problem = validateCollectionExport(form);
  const reasonMissing = form.reason.trim() === '';

  const handleDownload = async () => {
    if (problem) {
      setError(problemMessage(problem));
      return;
    }
    const ok = window.confirm(
      t(
        'orders.collectionExport.confirm',
        '개인정보가 포함된 파일입니다. 다운로드 기록(사유 포함)이 남습니다. 내려받으시겠습니까?',
      ),
    );
    if (!ok) return;
    setError(null);
    setDownloading(true);
    try {
      await downloadCollectionExport(form);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('orders.collectionExport.failed', '엑셀을 만들지 못했습니다.'));
    } finally {
      setDownloading(false);
    }
  };

  const labelCls = 'grid gap-1 text-xs';
  const muted = { color: 'var(--content-muted)' } as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]" data-testid="collection-export-dialog">
        <DialogHeader>
          <DialogTitle>{t('orders.collectionExport.title', '상품 추가입력 엑셀 다운로드')}</DialogTitle>
          <DialogDescription>
            {t(
              'orders.collectionExport.description',
              '주문 상품의 추가입력(입력옵션)을 개인정보 원문으로 내려받습니다. 상품 또는 기간 중 하나는 지정해야 하며, 최대 5,000행까지 받을 수 있습니다.',
            )}
          </DialogDescription>
        </DialogHeader>

        {/* QA-543 — 주문 목록 조건과 범위가 다르다(상품·기간, 기간=추가입력 저장 시각). 같은 조건 결과는 일반 엑셀 [상품 추가입력] 칸. */}
        <p
          className="rounded-md p-3 text-[12px]"
          style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
          data-testid="collection-export-scope-note"
        >
          {t(
            'orders.collectionExport.scopeNote',
            '주문 목록의 검색 조건과는 범위가 다릅니다. 여기서는 고른 상품·기간(추가입력을 저장한 시각 기준)의 입력만 나오고, 입력이 없는 주문은 빠집니다. 주문 목록과 같은 조건으로 받으려면 [엑셀 다운로드]에서 [상품 추가입력] 칸을 고르세요.',
          )}
        </p>

        <div className="flex flex-col gap-3 py-1">
          <div className={labelCls} style={muted}>
            <span>{t('orders.collectionExport.product', '상품')}</span>
            <ProductSelector
              value={form.productId}
              onChange={(id) => set('productId', id)}
              placeholder={t('orders.collectionExport.productPlaceholder', '상품명으로 검색 (선택)')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className={labelCls} style={muted}>
              {t('orders.collectionExport.from', '주문일 시작')}
              <Input type="date" value={form.from} onChange={(e) => set('from', e.target.value)} />
            </label>
            <label className={labelCls} style={muted}>
              {t('orders.collectionExport.to', '주문일 종료(포함)')}
              <Input type="date" value={form.to} onChange={(e) => set('to', e.target.value)} />
            </label>
          </div>
          <p className="text-[11px]" style={muted}>
            {t('orders.collectionExport.periodHint', {
              max: EXPORT_MAX_PERIOD_DAYS,
              defaultValue: '주문일(한국 시간) 기준입니다. 상품을 지정하지 않으면 최대 {{max}}일까지 고를 수 있습니다.',
            })}
          </p>

          <label className={labelCls} style={muted}>
            <span>
              {t('orders.collectionExport.reason', '다운로드 사유')}
              <span style={{ color: 'var(--signal-danger)' }}> *</span>
            </span>
            <Input
              value={form.reason}
              onChange={(e) => set('reason', e.target.value)}
              maxLength={EXPORT_REASON_MAX}
              placeholder={t('orders.collectionExport.reasonPlaceholder', '예: 9월 팬사인회 당첨자 명단 확인')}
              data-testid="collection-export-reason"
            />
          </label>

          <label className={labelCls} style={muted}>
            {t('orders.collectionExport.password', '엑셀 열기 비밀번호 (선택, 권장)')}
            <Input
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              maxLength={EXPORT_PASSWORD_MAX}
              placeholder={t('orders.collectionExport.passwordPlaceholder', {
                min: EXPORT_PASSWORD_MIN,
                max: EXPORT_PASSWORD_MAX,
                defaultValue: '{{min}}~{{max}}자',
              })}
              data-testid="collection-export-password"
            />
          </label>
          <p className="text-[11px]" style={muted}>
            {t(
              'orders.collectionExport.passwordHint',
              '비밀번호를 넣으면 엑셀을 열 때 비밀번호를 묻는 암호화 파일로 받습니다. 개인정보 보호를 위해 넣기를 권장합니다.',
            )}
          </p>

          {error ? (
            <p
              role="alert"
              className="rounded px-2 py-1.5 text-[12px]"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
            >
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={downloading}>
            {t('orders.collectionExport.cancel', '취소')}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void handleDownload()}
            disabled={downloading || reasonMissing}
            data-testid="collection-export-download"
          >
            {downloading
              ? t('orders.collectionExport.downloading', '만드는 중…')
              : t('orders.collectionExport.download', '다운로드')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
